import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Typography,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
  Tabs,
  Tab,
  Box,
  TextField,
  Modal,
  Paper,
  Slide,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle } from 'lucide-react';

function ProductsPage() {
  const [value, setValue] = useState(0);
  const [productsWithBarcode, setProductsWithBarcode] = useState([]);
  const [productsWithoutBarcode, setProductsWithoutBarcode] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [newBarcode, setNewBarcode] = useState('');
  const [openModal, setOpenModal] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [success, setSuccess] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await axios.get(
          'https://shopify-inv-tracker-backend-d0b1c1125141.herokuapp.com/api/inventory-counts/products-with-and-without-barcodes'
        );
        setProductsWithBarcode(response.data.productsWithBarcode);
        setProductsWithoutBarcode(response.data.productsWithoutBarcode);
      } catch (error) {
        setError('Failed to load products.');
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  const handleOpenModal = (product) => {
    setSelectedProduct(product);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setSelectedProduct(null);
    setNewBarcode('');
    setOpenModal(false);
  };

  const handleSaveBarcode = async () => {
    setSuccess(true);
    setOpenSnackbar(true);
    console.log(success);

    if (!newBarcode) {
      setError('Please enter a barcode.');
      return;
    }

    try {
      const response = await axios.put(
        `https://shopify-inv-tracker-backend-d0b1c1125141.herokuapp.com/api/inventory-counts/products/${selectedProduct.id}/update-barcode`,
        { barcode: newBarcode }
      );

      console.log(response);
      console.log(selectedProduct);
      console.log(selectedProduct.id, newBarcode, typeof selectedProduct.id, typeof newBarcode);

      setSnackbarMessage(`Barcode updated successfully for ${selectedProduct.title}`);
      setOpenSnackbar(true);

      if (!selectedProduct.barcode) {
        setProductsWithoutBarcode((prev) => prev.filter((p) => p.id !== selectedProduct.id));
      } else {
        setProductsWithBarcode((prev) =>
          prev.map((p) =>
            p.id === selectedProduct.id ? { ...p, barcode: newBarcode } : p
          )
        );
      }

      handleCloseModal();
      if (!selectedProduct.barcode) {
        window.location.reload();
      }

    } catch (error) {
      setError('Failed to update barcode.');
    }
  };

  const handleSnackbarClose = () => {
    setOpenSnackbar(false);
    setSuccess(false);
  };

  const handleTabChange = (event, newValue) => {
    setValue(newValue);
  };

  const filteredWithout = productsWithoutBarcode.filter(product => product.currentInventory > 0);

  return (
    <Box className="fade-in">
      <Button
        onClick={() => navigate(-1)}
        startIcon={<ArrowLeft size={16} />}
        sx={{ mb: 2, color: 'text.secondary', fontWeight: 500 }}
      >
        Back
      </Button>

      <Typography variant="h4" sx={{ fontWeight: 700, letterSpacing: '-0.02em', mb: 0.5 }}>
        Product Barcodes
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
        Keep your product barcodes organized
      </Typography>

      {error && (
        <Box sx={{ p: 2, mb: 2, borderRadius: 2, bgcolor: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)' }}>
          <Typography variant="body2" color="error">{error}</Typography>
        </Box>
      )}

      <Tabs
        value={value}
        onChange={handleTabChange}
        sx={{
          mb: 3,
          '& .MuiTabs-indicator': { backgroundColor: '#2383e2' },
          '& .MuiTab-root': { textTransform: 'none', fontWeight: 500 },
        }}
      >
        <Tab label={`Without Barcodes (${filteredWithout.length})`} />
        <Tab label={`With Barcodes (${productsWithBarcode.length})`} />
      </Tabs>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={28} thickness={3} />
        </Box>
      ) : (
        <>
          {/* Tab 0: Without Barcodes */}
          {value === 0 && (
            filteredWithout.length === 0 ? (
              <Box sx={{ py: 4, textAlign: 'center', border: '1px dashed', borderColor: 'divider', borderRadius: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  All products have barcodes or have zero inventory.
                </Typography>
              </Box>
            ) : (
              <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
                <Box sx={{
                  display: 'grid', gridTemplateColumns: '50px 2fr 1fr 1fr',
                  px: 2.5, py: 1.5,
                  borderBottom: '1px solid', borderColor: 'divider',
                  bgcolor: 'action.hover',
                }}>
                  {['#', 'Product Name', 'Inventory', 'Actions'].map(h => (
                    <Typography key={h} variant="caption" sx={{
                      fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em',
                      fontSize: '0.7rem', color: 'text.secondary',
                    }}>{h}</Typography>
                  ))}
                </Box>
                {filteredWithout.map((product, index) => (
                  <Box key={product.id} sx={{
                    display: 'grid', gridTemplateColumns: '50px 2fr 1fr 1fr',
                    px: 2.5, py: 1.5,
                    borderBottom: '1px solid', borderColor: 'divider',
                    alignItems: 'center',
                    '&:last-child': { borderBottom: 'none' },
                    '&:hover': { bgcolor: 'action.hover' },
                  }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>{index + 1}</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{product.title}</Typography>
                    <Typography variant="body2">{product.currentInventory}</Typography>
                    <Button
                      size="small"
                      variant="contained"
                      onClick={() => handleOpenModal(product)}
                      sx={{ bgcolor: '#2383e2', '&:hover': { bgcolor: '#1b6ec2' }, justifySelf: 'start' }}
                    >
                      Add Barcode
                    </Button>
                  </Box>
                ))}
              </Box>
            )
          )}

          {/* Tab 1: With Barcodes */}
          {value === 1 && (
            <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
              <Box sx={{
                display: 'grid', gridTemplateColumns: '50px 2fr 1.5fr 1fr 1fr',
                px: 2.5, py: 1.5,
                borderBottom: '1px solid', borderColor: 'divider',
                bgcolor: 'action.hover',
              }}>
                {['#', 'Product Name', 'Barcode', 'Inventory', 'Actions'].map(h => (
                  <Typography key={h} variant="caption" sx={{
                    fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em',
                    fontSize: '0.7rem', color: 'text.secondary',
                  }}>{h}</Typography>
                ))}
              </Box>
              {productsWithBarcode.map((product, index) => (
                <Box key={product.id} sx={{
                  display: 'grid', gridTemplateColumns: '50px 2fr 1.5fr 1fr 1fr',
                  px: 2.5, py: 1.5,
                  borderBottom: '1px solid', borderColor: 'divider',
                  alignItems: 'center',
                  '&:last-child': { borderBottom: 'none' },
                  '&:hover': { bgcolor: 'action.hover' },
                }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>{index + 1}</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{product.title}</Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>{product.barcode}</Typography>
                  <Typography variant="body2">{product.currentInventory}</Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => handleOpenModal(product)}
                    sx={{ justifySelf: 'start' }}
                  >
                    Update
                  </Button>
                </Box>
              ))}
            </Box>
          )}
        </>
      )}

      <Modal open={openModal} onClose={handleCloseModal} closeAfterTransition>
        <Slide direction="down" in={openModal} mountOnEnter unmountOnExit>
          <Paper sx={{ p: 4, width: 400, margin: 'auto', mt: '20vh', borderRadius: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              {selectedProduct?.barcode
                ? `Update Barcode for ${selectedProduct?.title}`
                : `Add Barcode for ${selectedProduct?.title}`}
            </Typography>
            <TextField
              label="Barcode"
              variant="outlined"
              fullWidth
              sx={{ mb: 2 }}
              value={newBarcode}
              onChange={(e) => setNewBarcode(e.target.value)}
            />
            <Button
              variant="contained"
              fullWidth
              onClick={handleSaveBarcode}
              sx={{ bgcolor: '#2383e2', '&:hover': { bgcolor: '#1b6ec2' }, py: 1.25 }}
            >
              Save
            </Button>
          </Paper>
        </Slide>
      </Modal>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity="success"
          sx={{ width: '100%' }}
          icon={<CheckCircle size={18} />}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

export default ProductsPage;
